export type ResourceAliasMap = ReadonlyMap<string, string>;

export const flattenResourceSegment = (segment: string): string =>
  segment.toLowerCase().replace(/[^a-z0-9]/g, '');

export const kebabResourceSegment = (segment: string): string =>
  segment
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/_/g, '-')
    .toLowerCase();

const lastPathSegment = (path: string): string =>
  path.split('/').filter(Boolean).at(-1) ?? '';

export const buildResourceAliasMap = (targets: string[]): ResourceAliasMap => {
  const aliases = new Map<string, string>();

  for (const target of targets) {
    const alias = flattenResourceSegment(lastPathSegment(target));

    if (!alias) {
      throw new Error(
        `Resource alias target has no segment to alias: ${target}`,
      );
    }

    const existing = aliases.get(alias);
    if (existing && existing !== target) {
      throw new Error(
        `Resource alias "${alias}" maps to both ${existing} and ${target}`,
      );
    }

    aliases.set(alias, target);
  }

  return aliases;
};

export const resolveResourceAliasTarget = (
  aliases: ResourceAliasMap,
  segment: string,
): string | undefined => {
  const registered = aliases.get(flattenResourceSegment(segment));
  if (registered) {
    return registered;
  }

  const kebab = kebabResourceSegment(segment);
  if (kebab !== segment) {
    return `/${kebab}`;
  }

  const flattened = flattenResourceSegment(segment);
  if (flattened !== segment) {
    return `/${flattened}`;
  }

  return undefined;
};
